import { forwardRef, useId, useImperativeHandle, useMemo, useRef, useState, type ClipboardEvent, type KeyboardEvent, type ReactNode } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Avatar } from '@/ui/Avatar';
import { Icon } from '@/ui/Icon';
import { useDebounced } from '@/lib/hooks';
import type { ContactSuggestion } from '@shared/mail';
import { fetchContacts, keys } from './data';
import { dedupeRecipients, parseAddresses, type Rcpt } from './logic';

// One address row (To, Cc or Bcc): chips for the people added, and a box that suggests contacts as you type.

export interface RecipientsHandle {
  focus: () => void;
  /** Adds whatever valid address is still typed in the box. Returns the people added and the text left that is not an address. */
  commit: () => { added: Rcpt[]; left: string };
}

interface Props {
  label: 'To' | 'Cc' | 'Bcc';
  list: Rcpt[];
  onChange: (next: Rcpt[]) => void;
  /** Why Clientbook refuses an address, by lower-case address. */
  problems: Map<string, string>;
  /** Addresses already in the other rows. */
  taken: string[];
  hues: Map<string, number>;
  invalid?: boolean;
  shake?: boolean;
  aside?: ReactNode;
  onTyped?: () => void;
}

export const Recipients = forwardRef<RecipientsHandle, Props>(function Recipients({ label, list, onChange, problems, taken, hues, invalid, shake, aside, onTyped }, ref) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const q = useDebounced(query.trim(), 140);

  const { data, isFetching, isError } = useQuery({
    queryKey: keys.contacts(q),
    queryFn: ({ signal }) => fetchContacts(q, signal),
    enabled: open && q.length > 0,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  const used = useMemo(() => new Set([...list.map((r) => r.email.toLowerCase()), ...taken.map((e) => e.toLowerCase())]), [list, taken]);
  const suggestions = useMemo(() => (q ? (data ?? []).filter((s) => !used.has(s.email.toLowerCase())) : []), [data, used, q]);
  const typed = parseAddresses(query);
  const showList = open && query.trim().length > 0 && (suggestions.length > 0 || (!isFetching && !!data) || isError);

  const add = (items: Rcpt[]): Rcpt[] => {
    const fresh = dedupeRecipients(items, used);
    if (fresh.length) onChange([...list, ...fresh]);
    onTyped?.();
    return fresh;
  };

  const commit = () => {
    const r = parseAddresses(query);
    const added = r.valid.length ? add(r.valid) : [];
    const left = r.invalid.join(', ');
    setQuery(left);
    return { added, left };
  };

  useImperativeHandle(ref, () => ({ focus: () => input.current?.focus(), commit }));

  const pick = (s: ContactSuggestion) => {
    add([{ email: s.email, name: s.name, personId: s.personId }]);
    setQuery('');
    setActive(0);
    input.current?.focus();
  };

  const remove = (email: string) => {
    onChange(list.filter((r) => r.email !== email));
    onTyped?.();
    input.current?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    const current = showList ? suggestions[active] : undefined;
    if (e.key === 'ArrowDown' && showList) {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp' && showList) {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (current) {
        e.preventDefault();
        pick(current);
      } else if (typed.valid.length) {
        e.preventDefault();
        commit();
      }
    } else if (e.key === 'Tab' && !e.shiftKey && query.trim()) {
      if (current) {
        e.preventDefault();
        pick(current);
      } else commit();
    } else if ((e.key === ',' || e.key === ';') && typed.valid.length && !query.includes('"')) {
      e.preventDefault();
      commit();
    } else if (e.key === 'Backspace' && !query && list.length) {
      e.preventDefault();
      onChange(list.slice(0, -1));
      onTyped?.();
    } else if (e.key === 'Escape' && showList) {
      // Closes the suggestions without closing the composer.
      e.preventDefault();
      setOpen(false);
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text/plain');
    if (!/[,;\n]/.test(text)) return;
    e.preventDefault();
    const r = parseAddresses(`${query} ${text}`);
    if (r.valid.length) add(r.valid);
    setQuery(r.invalid.join(', '));
  };

  const listId = `${id}-list`;
  const optionId = (i: number) => `${id}-opt-${i}`;
  const single = list.length === 1 && !query ? list[0] : null;

  return (
    <div
      className={['cmp-row', 'cmp-rcpts', invalid ? 'invalid' : '', shake ? 'shake' : ''].filter(Boolean).join(' ')}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget || (e.target as HTMLElement).classList.contains('rv')) {
          e.preventDefault();
          input.current?.focus();
        }
      }}
    >
      <label className="rl" htmlFor={id}>
        {label}
      </label>
      <span className="rv">
        {list.map((r) => {
          const problem = problems.get(r.email.toLowerCase());
          return (
            <span key={r.email} className={['rcpt', problem ? 'bad' : ''].filter(Boolean).join(' ')} title={problem ?? r.email}>
              <Avatar name={r.name || r.email} hue={hues.get(r.email.toLowerCase())} className="av" />
              {r.name || r.email}
              <button type="button" className="rcpt-x" tabIndex={-1} aria-label={`Remove ${r.name || r.email}`} onClick={() => remove(r.email)}>
                <Icon name="x" />
              </button>
            </span>
          );
        })}
        {single?.name ? <span className="rcpt-addr">{single.email}</span> : null}
        <input
          ref={input}
          id={id}
          value={query}
          placeholder={list.length ? '' : 'Name or email address'}
          autoComplete="off"
          spellCheck={false}
          role="combobox"
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={showList && suggestions[active] ? optionId(active) : undefined}
          aria-invalid={invalid || undefined}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
            setOpen(true);
            onTyped?.();
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            setOpen(false);
            if (typed.valid.length) commit();
          }}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
        />
      </span>
      {aside}
      {showList ? (
        <div className="cmp-sugg" role="listbox" id={listId} aria-label={`Suggestions for ${label}`}>
          {suggestions.map((s, i) => (
            <div
              key={s.email}
              id={optionId(i)}
              role="option"
              aria-selected={i === active}
              className={['sg', i === active ? 'on' : ''].filter(Boolean).join(' ')}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(s);
              }}
              onMouseEnter={() => setActive(i)}
            >
              <Avatar name={s.name} hue={s.hue} size="sm" />
              <span className="grow">
                <b>{s.name}</b>
                <span>
                  {s.email}
                  {s.company ? ` · ${s.company}` : ''}
                </span>
                {s.problem ? <em>{s.problem}</em> : null}
              </span>
            </div>
          ))}
          {suggestions.length === 0 ? (
            <div className="sg-empty">
              {isError
                ? 'Suggestions are not loading. Type the full address.'
                : typed.valid.length
                  ? 'Press Enter to add this address'
                  : 'No contacts match. Type the full address.'}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
});
